import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type { Criteria } from "../model/Criteria";
import type { SortOrder } from "../model/Feature";

const es = global.es;

/**
 * @class ScheduledUpdateModule
 */
export default class ScheduledUpdateModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    readonly STATUS = {
        "OK": "OK",
        "IN_PROGRESS": "IN_PROGRESS",
        "USER_DISABLED": "USER_DISABLED",
        "SYSTEM_DISABLED": "SYSTEM_DISABLED",
        "IMPORT_FAIL_WITH_REASON": "IMPORT_FAIL_WITH_REASON",
        "RUNTIME_ERROR": "RUNTIME_ERROR"
    };
    
    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.run = this.run.bind(this);
        this.remove = this.remove.bind(this);
        this.removeAll = this.removeAll.bind(this);
        this.getLogs = this.getLogs.bind(this);
        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.list = this.list.bind(this);
        this.getByLayerMetaId = this.getByLayerMetaId.bind(this);
        this.count = this.count.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
    }

    /**
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    run(id: number, opts?: ApiOpts) {
        if (!id) {
            throw new Error("Invalid id specified for es.api.scheduledUpdate#run", id);
        }

        const url = es.api.getURL(this.resourceName) + "/" + id + "/run";

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: null
        });
    }
    
    /**
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clear(this.resourceName, id);
        });
    }

    /**
     * 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    removeAll(ids: number[], opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);

        return this.rqBroker.doDelete({
            url: url,
            opts: opts,
            data: null
        });
    }
    
    /**
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getLogs(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id + "/log";

        return this.rqBroker.doGet({url: url, opts: opts}); // no-cache
    }

    /**
     * Get the resource from the server
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param ids
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);

        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, opts?: ApiOpts) {
        const url = es.api.getListQueryURL(this.resourceName, criteria, sortOrder, offset, length);

        return this.rqBroker.doGet({url: url});
        //return cache.doGetList(rqBroker, resourceName, criteria, sortOrder, offset, length, opts);
    }

    /**
     * List existing SUs already associated with a layerMetaId
     * 
     * @param layerMetaId
     * @param opts
     * 
     * @returns
     */
    getByLayerMetaId(layerMetaId: number, opts?: ApiOpts) {
        const criteria: Criteria = {
            $type: "Equal",
            attr: "layerMetaId",
            value: layerMetaId
        };

        return this.list(criteria, null, null, null, opts);
    }

    /**
     * Get a count of resources matching the given Criteria
     * 
     * @param criteria
     * @param opts
     * 
     * @returns long
     */
    count(criteria?: Criteria, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");

        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj e.g. 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName);

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
    
    /**
     * PUT update a resource (note: id attrib must be valid)
     * 
     * @param resourceObj e.g. 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + resourceObj.id;

        return this.rqBroker.doPut({
            url: url,
            data: resourceObj,
            opts: opts
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
        });
    }
}
